import axios from 'axios';

const RAPIDAPI_KEY = import.meta.env?.VITE_RAPIDAPI_KEY;
const RAPIDAPI_HOST = import.meta.env?.VITE_RAPIDAPI_HOST;

const astrologyApi = axios?.create({
  baseURL: `https://${RAPIDAPI_HOST}`,
  headers: {
    'X-RapidAPI-Key': RAPIDAPI_KEY,
    'X-RapidAPI-Host': RAPIDAPI_HOST
  }
});

const convertTimezoneToOffset = (timezone) => {
  const match = timezone?.match(/UTC([+-]?\d+)/);
  if (match) {
    return parseFloat(match?.[1]);
  }
  return 0;
};

const formatDateForAPI = (birthDate, birthTime, unknownTime) => {
  const date = new Date(birthDate);
  const day = date?.getDate();
  const month = date?.getMonth() + 1;
  const year = date?.getFullYear();
  
  let hour = 12;
  let minute = 0;
  
  if (!unknownTime && birthTime?.hour && birthTime?.minute) {
    hour = parseInt(birthTime?.hour);
    minute = parseInt(birthTime?.minute);
  }
  
  return { day, month, year, hour, minute };
};

const mapZodiacSign = (degree) => {
  const zodiacSigns = [
    { nameTh: 'เมษ', nameEn: 'Aries', start: 0 },
    { nameTh: 'พฤษภ', nameEn: 'Taurus', start: 30 },
    { nameTh: 'เมถุน', nameEn: 'Gemini', start: 60 },
    { nameTh: 'กรกฎ', nameEn: 'Cancer', start: 90 },
    { nameTh: 'สิงห์', nameEn: 'Leo', start: 120 },
    { nameTh: 'กันย์', nameEn: 'Virgo', start: 150 },
    { nameTh: 'ตุลย์', nameEn: 'Libra', start: 180 },
    { nameTh: 'พิจิก', nameEn: 'Scorpio', start: 210 },
    { nameTh: 'ธนู', nameEn: 'Sagittarius', start: 240 },
    { nameTh: 'มังกร', nameEn: 'Capricorn', start: 270 },
    { nameTh: 'กุมภ์', nameEn: 'Aquarius', start: 300 },
    { nameTh: 'มีน', nameEn: 'Pisces', start: 330 }
  ];
  
  const normalizedDegree = parseFloat(degree) % 360;
  for (let i = zodiacSigns?.length - 1; i >= 0; i--) {
    if (normalizedDegree >= zodiacSigns?.[i]?.start) {
      return zodiacSigns?.[i];
    }
  }
  return zodiacSigns?.[0];
};

const getPlanetNameThai = (planetEn) => {
  const planetNames = {
    'Sun': 'ดวงอาทิตย์',
    'Moon': 'ดวงจันทร์',
    'Mercury': 'พุธ',
    'Venus': 'ดาวศุกร์',
    'Mars': 'ดาวอังคาร',
    'Jupiter': 'ดาวพฤหัสบดี',
    'Saturn': 'ดาวเสาร์',
    'Uranus': 'ดาวยูเรนัส',
    'Neptune': 'ดาวเนปจูน',
    'Pluto': 'ดาวพลูโต'
  };
  return planetNames?.[planetEn] || planetEn;
};

const getAspectNameThai = (aspectEn) => {
  const aspectNames = {
    'Conjunction': 'สหัสสะ',
    'Opposition': 'ตรงข้าม',
    'Trine': 'สามเหลี่ยม',
    'Square': 'สี่เหลี่ยม',
    'Sextile': 'หกเหลี่ยม',
    'Quincunx': 'ควินคังซ์',
    'Semi-square': 'เซมิสแควร์',
    'Sesquiquadrate': 'เซสควิควอดเรต'
  };
  return aspectNames?.[aspectEn] || aspectEn;
};

export const getBirthChartData = async (formData) => {
  try {
    const { day, month, year, hour, minute } = formatDateForAPI(
      formData?.birthDate,
      formData?.birthTime,
      formData?.unknownTime
    );
    
    const timezoneOffset = convertTimezoneToOffset(formData?.timezone);
    
    const latitude = formData?.latitude ? parseFloat(formData?.latitude) : 13.7563;
    const longitude = formData?.longitude ? parseFloat(formData?.longitude) : 100.5018;
    
    const response = await astrologyApi?.post('/birth-chart-data', {
      day,
      month,
      year,
      hour,
      minute,
      latitude,
      longitude,
      timezone: timezoneOffset,
      house_system: formData?.houseSystem || 'placidus'
    });
    
    const apiData = response?.data;
    
    const planetaryPositions = [];
    const planets = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto'];
    
    planets?.forEach(planet => {
      if (apiData?.planets?.[planet]) {
        const planetData = apiData?.planets?.[planet];
        const zodiac = mapZodiacSign(planetData?.longitude);
        planetaryPositions?.push({
          nameTh: getPlanetNameThai(planet),
          nameEn: planet,
          degree: planetData?.longitude?.toFixed(2) || '0.00',
          zodiacTh: zodiac?.nameTh,
          zodiacEn: zodiac?.nameEn,
          house: planetData?.house?.toString() || '1'
        });
      }
    });
    
    const houseCusps = [];
    if (apiData?.houses) {
      for (let i = 1; i <= 12; i++) {
        const houseKey = `house_${i}`;
        if (apiData?.houses?.[houseKey]) {
          const houseDegree = apiData?.houses?.[houseKey];
          const zodiac = mapZodiacSign(houseDegree);
          houseCusps?.push({
            houseNumber: i?.toString(),
            cuspDegree: houseDegree?.toFixed(2) || '0.00',
            zodiacTh: zodiac?.nameTh,
            zodiacEn: zodiac?.nameEn
          });
        }
      }
    }
    
    const aspects = [];
    if (apiData?.aspects && Array.isArray(apiData?.aspects)) {
      apiData?.aspects?.forEach(aspect => {
        const isMajor = ['Conjunction', 'Opposition', 'Trine', 'Square', 'Sextile']?.includes(aspect?.type);
        aspects?.push({
          planetA: aspect?.planet1 || '',
          aspectTypeTh: getAspectNameThai(aspect?.type),
          aspectTypeEn: aspect?.type || '',
          planetB: aspect?.planet2 || '',
          orb: aspect?.orb?.toFixed(2) || '0.00',
          isMajor
        });
      });
    }
    
    const midpoints = [];
    const uranianPoints = {
      fatePoints: [],
      hypotheticalPlanets: []
    };
    const dignities = [];
    
    if (apiData?.ascendant) {
      const ascZodiac = mapZodiacSign(apiData?.ascendant);
      uranianPoints?.fatePoints?.push({
        nameTh: 'จุดขึ้น (ASC)',
        nameEn: 'Ascendant',
        degree: apiData?.ascendant?.toFixed(2),
        zodiacTh: ascZodiac?.nameTh,
        zodiacEn: ascZodiac?.nameEn
      });
    }
    
    if (apiData?.midheaven) {
      const mcZodiac = mapZodiacSign(apiData?.midheaven);
      uranianPoints?.fatePoints?.push({
        nameTh: 'จุดสูงสุด (MC)',
        nameEn: 'Midheaven',
        degree: apiData?.midheaven?.toFixed(2),
        zodiacTh: mcZodiac?.nameTh,
        zodiacEn: mcZodiac?.nameEn
      });
    }
    
    return {
      success: true,
      data: {
        planetaryPositions,
        houseCusps,
        midpoints,
        aspects,
        uranianPoints,
        dignities
      },
      rawApiResponse: apiData
    };
  } catch (error) {
    console.error('Astrology API Error:', error);
    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Failed to fetch birth chart data',
      errorDetails: error?.response?.data
    };
  }
};

export default {
  getBirthChartData
};